import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListLearningPathwaysQuery, useGetLearningPathwayQuery } from 'src/redux/slices/api/su';
import { selectLearningPathwayById } from 'src/redux/slices/learningPathways';
import { useAppSelector } from 'src/redux/hooks';
import { LearningPathwayBanner } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { t, resolveContent } from 'src/utils/translation';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID, CORE_STRING_ID } from 'src/constants/localization';
import { ROUTES } from 'src/constants/route';
import { getAppRoute } from 'src/utils/routing';
import { LearningPathwayState } from 'src/types/state/learningPathway';
import { useClientId, useSelLocale, UNGATED_CLIENT_ID } from 'src/contexts/ClientContext';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';

interface LearningPathwayBannersSectionProps {
  learningPathwayIds: string[];
}

/**
 * Renders a single learning pathway banner using PolyD API data.
 * Extracted as a separate component so each pathway can call useGetLearningPathwayQuery independently.
 */
const PolyDBanner: React.FC<{
  learningPathwayId: string;
  clientId: string;
}> = ({ learningPathwayId, clientId }) => {
  const navigate = useNavigate();
  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);
  const [coreBundle] = useBundle(BUNDLE_NAME.CORE);
  const pathway = useAppSelector(
    (rootState) => selectLearningPathwayById(rootState, learningPathwayId),
  );

  const { isLoading, isError } = useGetLearningPathwayQuery({
    learningPathwayId,
    clientId,
    metadataOnly: true,
  });

  const bannerProps = useMemo(() => {
    if (isLoading || isError || !pathway?.data) return null;

    const { data } = pathway;
    const totalDuration = data.learningPathwayStats?.totalDuration;
    const durationMinutes = (totalDuration && Number.isFinite(totalDuration) && totalDuration > 0)
      ? Math.round(totalDuration / 60)
      : 0;

    return {
      headerText: t(learningPathwayBundle, LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_FEATURED_HEADER_TEXT),
      title: data.title,
      courseType: t(learningPathwayBundle, LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PAGE_HEADER_PATH_TYPE),
      metadata: [
        t(learningPathwayBundle, LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_DURATION, {
          minutes: durationMinutes,
        }),
      ],
      description: data.description,
      imageSrc: data.thumbnail || data.imageSrc || '',
      actionButton: {
        children: t(coreBundle, CORE_STRING_ID.ACTION_TAKE_COURSE),
        label: t(coreBundle, CORE_STRING_ID.ACTION_TAKE_COURSE),
        onClick: () => navigate(getAppRoute(`${ROUTES.LEARN.LEARNING_PATHWAY}/${data.learningPathwayId}`)),
      },
    };
  }, [pathway, isLoading, isError, learningPathwayBundle, coreBundle, navigate, learningPathwayId]);

  if (!bannerProps) return null;

  return <LearningPathwayBanner {...bannerProps} />;
};

/**
 * Displays learning pathway banners for the provided pathway IDs
 */
const LearningPathwayBannersSection: React.FC<LearningPathwayBannersSectionProps> = ({
  learningPathwayIds,
}) => {
  const navigate = useNavigate();
  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);
  const [coreBundle] = useBundle(BUNDLE_NAME.CORE);
  const learningPathwaysById = useAppSelector(
    (rootState) => rootState.learningPathways.byId,
  );
  const clientId = useClientId();
  const selLocale = useSelLocale();
  // Under T1, ungated users also use PolyD path (pre-localized content).
  const isPolyMigrationT1 = useAppSelector(
    (state) => selectIsWeblabInT1(state, WeblabId.NalandaPolyMigration),
  );
  const isLocalized = clientId !== UNGATED_CLIENT_ID || isPolyMigrationT1;

  const {
    data: learningPathwaysData,
    isLoading,
    isError,
  } = useListLearningPathwaysQuery({ clientId, selLocale }, { skip: isLocalized });

  const learningPathwayBanners = useMemo(() => {
    if (!learningPathwaysData?.learningPathways || !learningPathwayIds.length) return [];

    // Filter pathways that are in the provided IDs
    const relevantPathways = learningPathwaysData.learningPathways.filter(
      (pathway) => learningPathwayIds.includes(pathway.learningPathwayId),
    );

    return relevantPathways
      .map((pathway) => learningPathwaysById[pathway.learningPathwayId])
      .filter(Boolean)
      .map((pathway: LearningPathwayState) => {
        const { data } = pathway;

        return {
          headerText: t(learningPathwayBundle, LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_FEATURED_HEADER_TEXT),
          title: resolveContent(learningPathwayBundle, data.title, isLocalized),
          courseType: t(learningPathwayBundle, LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PAGE_HEADER_PATH_TYPE),
          metadata: [
            t(learningPathwayBundle, LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_DURATION, {
              minutes: Math.round((data.learningPathwayStats?.totalDuration || 0) / 60),
            }),
          ],
          description: resolveContent(learningPathwayBundle, data.description, isLocalized),
          imageSrc: data.thumbnail || data.imageSrc || '',
          actionButton: {
            children: t(coreBundle, CORE_STRING_ID.ACTION_TAKE_COURSE),
            label: t(coreBundle, CORE_STRING_ID.ACTION_TAKE_COURSE),
            onClick: () => navigate(getAppRoute(`${ROUTES.LEARN.LEARNING_PATHWAY}/${data.learningPathwayId}`)),
          },
        };
      });
  }, [learningPathwaysData, learningPathwayIds, learningPathwaysById, learningPathwayBundle, coreBundle, isLocalized, navigate]);

  // When PolyD is active, use individual PolyD API calls for each pathway
  if (isLocalized) {
    if (!learningPathwayIds.length) return null;

    return (
      <div className="flex flex-col gap-su-space-08">
        {learningPathwayIds.map((id) => (
          <PolyDBanner key={id} learningPathwayId={id} clientId={clientId} />
        ))}
      </div>
    );
  }

  // For ungated users, use existing useListLearningPathwaysQuery + resolveContent path
  if (isLoading || isError || learningPathwayBanners.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-su-space-08">
      {learningPathwayBanners.map((bannerProps) => (
        <LearningPathwayBanner key={bannerProps.title} {...bannerProps} />
      ))}
    </div>
  );
};

export default LearningPathwayBannersSection;
